import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AdminLayout from "../components/layout/AdminLayout";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  HelpCircle,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Package,
  User,
  Calendar,
  DollarSign,
  Store,
  FileText,
  RefreshCw,
} from "lucide-react";

const STATUS_CONFIG = {
  new: { label: "New", color: "bg-blue-100 text-blue-800", icon: HelpCircle },
  in_progress: { label: "In Progress", color: "bg-amber-100 text-amber-800", icon: Clock },
  resolved: { label: "Resolved", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  closed: { label: "Closed", color: "bg-slate-100 text-slate-800", icon: XCircle },
};

export default function AdminHelpRequestsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchRequests();
  }, [user, statusFilter]);

  const fetchRequests = async () => {
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const response = await axios.get(`${API}/admin/help-requests${params}`, {
        withCredentials: true,
      });
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error("Error fetching help requests:", error);
      toast.error("Failed to load help requests");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (requestId, newStatus) => {
    setUpdating(requestId);
    try {
      await axios.patch(
        `${API}/admin/help-requests/${requestId}?status=${newStatus}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus].label}`);
      fetchRequests();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    } finally {
      setUpdating(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <HelpCircle className="h-6 w-6 text-blue-600" />
              Help Requests
            </h1>
            <p className="text-slate-600 mt-1">
              Customer requests for product sourcing assistance
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchRequests} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          {Object.entries(STATUS_CONFIG).map(([status, config]) => {
            const count = requests.filter((r) => r.status === status).length;
            const Icon = config.icon;
            return (
              <div
                key={status}
                className="bg-white border border-slate-200 rounded-lg p-4 cursor-pointer hover:border-slate-300"
                onClick={() => setStatusFilter(status)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{count}</p>
                    <p className="text-sm text-slate-600">{config.label}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Requests List */}
        {requests.length === 0 ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
            <HelpCircle className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No help requests found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((req) => {
              const statusConfig = STATUS_CONFIG[req.status] || STATUS_CONFIG.new;
              const StatusIcon = statusConfig.icon;
              
              return (
                <div
                  key={req.request_id}
                  className="bg-white border border-slate-200 rounded-lg p-6"
                  data-testid={`help-request-${req.request_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      {/* Header */}
                      <div className="flex items-center gap-3">
                        <Badge className={statusConfig.color}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                        <span className="text-xs text-slate-500 font-mono">
                          {req.request_id}
                        </span>
                      </div>

                      {/* Product Name */}
                      <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                        <Package className="h-5 w-5 text-slate-400" />
                        {req.product_name}
                      </h3>

                      {/* Details Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center gap-2 text-slate-600">
                          <User className="h-4 w-4 text-slate-400" />
                          {req.user_email || "Guest"}
                        </div>
                        <div className="flex items-center gap-2 text-slate-600">
                          <Calendar className="h-4 w-4 text-slate-400" />
                          {formatDate(req.created_at)}
                        </div>
                        {req.desired_quantity && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Package className="h-4 w-4 text-slate-400" />
                            Qty: {req.desired_quantity}
                          </div>
                        )}
                        {req.budget_range && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <DollarSign className="h-4 w-4 text-slate-400" />
                            {req.budget_range}
                          </div>
                        )}
                        {req.preferred_marketplace && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Store className="h-4 w-4 text-slate-400" />
                            {req.preferred_marketplace}
                          </div>
                        )}
                      </div>

                      {/* Notes */}
                      {req.notes && (
                        <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-700">
                          <FileText className="h-4 w-4 inline mr-2 text-slate-400" />
                          {req.notes}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex-shrink-0">
                      <Select
                        value={req.status}
                        onValueChange={(value) => updateStatus(req.request_id, value)}
                        disabled={updating === req.request_id}
                      >
                        <SelectTrigger className="w-[140px]">
                          {updating === req.request_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <SelectValue />
                          )}
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
